/**
 * Clears legacy JWT keys and user-related cached data from localStorage.
 * Auth tokens now live in httpOnly cookies — JS must never store them.
 * Used by authApi.logout() and baseApi session-expiration handler.
 */

const LEGACY_AUTH_KEYS = [
  'auth_token',
  'refresh_token',
  'access',
  'access_token',
] as const;

const AUTH_BROADCAST_CHANNEL = 'aralin-auth';
const AUTH_NOTICE_KEY = 'aralin-auth-notice';

export type AuthClearedReason = 'logout' | 'password_changed';

export const AUTH_NOTICE_PASSWORD_CHANGED = 'password_changed';
export const PASSWORD_CHANGED_LOGIN_MESSAGE =
  'Password changed — please log in again.';

export function setAuthNotice(notice: string): void {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(AUTH_NOTICE_KEY, notice);
}

export function getAuthNotice(): string | null {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem(AUTH_NOTICE_KEY);
}

export function clearAuthNotice(): void {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(AUTH_NOTICE_KEY);
}

export function clearLegacyAuthTokens(): void {
  if (typeof window === 'undefined') return;
  for (const key of LEGACY_AUTH_KEYS) {
    localStorage.removeItem(key);
  }
}

export function broadcastAuthCleared(reason: AuthClearedReason = 'logout'): void {
  if (typeof window === 'undefined') return;
  try {
    const channel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
    channel.postMessage({ type: 'logout', reason });
    channel.close();
  } catch {
    // BroadcastChannel unsupported — other tabs will notice on next API call
  }
}

export function broadcastNoticeCleared(): void {
  if (typeof window === 'undefined') return;
  try {
    const channel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
    channel.postMessage({ type: 'notice_cleared' });
    channel.close();
  } catch {
    // BroadcastChannel unsupported
  }
}

export function subscribeAuthCleared(
  onCleared: (reason: AuthClearedReason) => void
): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }

  try {
    const channel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
    channel.onmessage = (event: MessageEvent) => {
      if (event.data?.type === 'logout') {
        const reason: AuthClearedReason =
          event.data.reason === AUTH_NOTICE_PASSWORD_CHANGED
            ? 'password_changed'
            : 'logout';
        onCleared(reason);
      }
    };
    return () => channel.close();
  } catch {
    return () => {};
  }
}

export function subscribeNoticeCleared(onCleared: () => void): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }

  try {
    const channel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
    channel.onmessage = (event: MessageEvent) => {
      if (event.data?.type === 'notice_cleared') {
        onCleared();
      }
    };
    return () => channel.close();
  } catch {
    return () => {};
  }
}

export function clearAuthData(reason: AuthClearedReason = 'logout'): void {
  if (typeof window === 'undefined') return;

  clearLegacyAuthTokens();

  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (
      key &&
      (key.startsWith('session-report-') ||
        key.startsWith('exam-timer-') ||
        key.startsWith('answers-') ||
        key.startsWith('exam-state-') ||
        key.startsWith('exam-session-') ||
        key.startsWith('exam-') ||
        key.startsWith('session-completed-'))
    ) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));

  broadcastAuthCleared(reason);
}
